<template>
    <div>
        <form-validation v-slot="{ bindings: formValidationBindings }">
            <flex-container v-if="goal && form" :padding="Size.md">
                <flex-container direction="column">
                    <text-heading>
                        Eigen doel
                    </text-heading>
                    <text-description>
                        Er is automatisch een doelnaam en set van velden gegenereerd.
                        Pas deze naar wens aan voordat je je doel activeert.
                    </text-description>
                </flex-container>
                <input-group v-model="form.name" :schema="string().required()">
                    <template #label>
                        <input-label>
                            Doelnaam
                        </input-label>
                    </template>
                    <template #input>
                        <text-input />
                    </template>
                </input-group>
                <input-group v-model="form.recipientEmail" :schema="string().email().required()">
                    <template #label>
                        <input-label>
                            E-mailadres voor het ontvangen van resultaten
                        </input-label>
                    </template>
                    <template #input>
                        <text-input />
                    </template>
                </input-group>
                <inline-tabs selected-name="fields">
                    <template #labels>
                        <inline-tab-label name="fields">
                            Velden
                        </inline-tab-label>
                        <inline-tab-label name="advanced">
                            Geavanceerd
                        </inline-tab-label>
                    </template>
                    <template #panels>
                        <inline-tab-panel name="fields" with-variable-height>
                            <flex-container :gap="Size.sm">
                                <flex-container>
                                    <flex-container :size="4">
                                        <text-heading sub>
                                            Veld
                                        </text-heading>
                                    </flex-container>
                                    <flex-container :size="6">
                                        <div class="goal-form__radio-header">
                                            <text-heading sub>
                                                Verplicht
                                            </text-heading>
                                            <text-heading sub>
                                                Optioneel
                                            </text-heading>
                                            <text-heading sub>
                                                Niet vragen
                                            </text-heading>
                                        </div>
                                    </flex-container>
                                </flex-container>

                                <horizontal-line no-margin />

                                <template v-for="field in GoalFieldName.values">
                                    <input-group
                                        v-if="fieldValues[field.value]"
                                        :key="field.value"
                                        v-model="fieldValues[field.value]"
                                        style="width: 100%;"
                                        inline
                                    >
                                        <template #input>
                                            <flex-container align-items="flex-end">
                                                <flex-container :size="4">
                                                    <text-decorator>
                                                        {{ field.label }}
                                                    </text-decorator>
                                                </flex-container>
                                                <flex-container :size="6">
                                                    <radio-group-input
                                                        :items="[
                                                            { value: 'required' },
                                                            { value: 'optional' },
                                                            { value: 'no' },
                                                        ]"
                                                        :columns="3"
                                                        justify-columns="center"
                                                    />
                                                </flex-container>
                                            </flex-container>
                                        </template>
                                    </input-group>
                                </template>
                            </flex-container>
                        </inline-tab-panel>
                        <inline-tab-panel name="advanced" with-variable-height>
                            <flex-container :gap="Size.md">
                                <input-group
                                    v-model="form.functionName"
                                    :schema="string().lowercase().required()"
                                >
                                    <template #label>
                                        <input-label>
                                            Functienaam
                                        </input-label>
                                    </template>
                                    <template #input>
                                        <text-input :disabled="true" />
                                    </template>
                                </input-group>
                                <input-group v-model="form.functionPurpose" :schema="string().required()">
                                    <template #label>
                                        <input-label>
                                            Purpose
                                        </input-label>
                                        <div>
                                            <text-decorator size="xs" weight="light">
                                                Korte uitleg van het doel van de functie.
                                                Helpt de AI begrijpen wanneer deze functie relevant is om te gebruiken.
                                                Deze hebben wij automatisch gegenereerd.
                                            </text-decorator>
                                        </div>
                                    </template>
                                    <template #input>
                                        <text-area-input height="125px" />
                                    </template>
                                </input-group>
                                <input-group v-model="form.functionUsage" :schema="string().required()">
                                    <template #label>
                                        <input-label>
                                            Usage
                                        </input-label>
                                        <div>
                                            <text-decorator size="xs" weight="light">
                                                Beschrijft in welke situaties en op welke manier de functie moet worden toegepast.
                                                Biedt richtlijnen voor het gedrag van de AI.
                                                Deze hebben wij automatisch gegenereerd.
                                            </text-decorator>
                                        </div>
                                    </template>
                                    <template #input>
                                        <text-area-input height="125px" />
                                    </template>
                                </input-group>
                                <input-group v-model="form.functionDescription" :schema="string().required()">
                                    <template #label>
                                        <input-label>
                                            Description
                                        </input-label>
                                        <div>
                                            <text-decorator size="xs" weight="light">
                                                Geeft een algemene beschrijving van wat de functie doet.
                                                Wordt gebruikt als referentie voor interpretatie en werking.
                                                Deze hebben wij automatisch gegenereerd.
                                            </text-decorator>
                                        </div>
                                    </template>
                                    <template #input>
                                        <text-area-input height="125px" />
                                    </template>
                                </input-group>
                                <input-group v-model="form.successMessage" :schema="string()">
                                    <template #label>
                                        <input-label>
                                            Bericht na bereiken doel (optioneel)
                                        </input-label>
                                        <div>
                                            <text-decorator size="xs" weight="light">
                                                Welk bericht wil je meegeven aan de AI als het doel is bereikt?
                                                De AI kan dit vertalen naar de bezoeker.
                                            </text-decorator>
                                        </div>
                                    </template>
                                    <template #input>
                                        <text-area-input height="125px" />
                                    </template>
                                </input-group>
                            </flex-container>
                        </inline-tab-panel>
                    </template>
                </inline-tabs>

                <flex-container
                    no-wrap
                    justify-content="flex-end"
                    align-items="center"
                >
                    <button-text
                        @click="$router.push({ name: 'assistent-chat-edit-chat-connect' })"
                    >
                        Annuleren
                    </button-text>
                    <button-block :disabled="!formValidationBindings.isValid.value" @loading="save($event)">
                        Opslaan
                    </button-block>
                </flex-container>
            </flex-container>
        </form-validation>
    </div>
    <flex-container
        v-if="!goal"
        height="full"
        align-items="center"
        align-content="center"
    >
        <spinner-loader />
    </flex-container>
</template>

<script setup lang="ts">
    import { onMounted, ref } from 'vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { ChangeGoalCommand, ChatConnectGoal, GoalFieldName } from '@keyvue/one-api-client';
    import { ButtonState, type ButtonStateChangedEvent, NotificationBlock, Size, TextAreaInput } from '@keyvue/one-ui';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import { string } from 'yup';
    import { useRouter } from 'vue-router';

    const props = defineProps<{ goalId: string }>();

    const { client } = useClient();
    const { notify } = useNotify();
    const router = useRouter();

    const loading = ref(true);
    const goal = ref<ChatConnectGoal | null>(null);
    const fieldValues = ref<{ [key: string]: { value: string } }>({});
    const form = ref<ChangeGoalCommand>();

    async function load() {
        goal.value = await client.get(ChatConnectGoal, props.goalId);
        form.value = ChangeGoalCommand.fromGoal(goal.value as ChatConnectGoal);

        GoalFieldName.values.forEach((goalFieldName) => {
            fieldValues.value[goalFieldName.value] = { value: 'no' };
        });

        form.value.requiredFields.forEach((fieldName) => {
            fieldValues.value[fieldName.value] = { value: 'required' };
        });

        form.value.optionalFields.forEach((fieldName) => {
            fieldValues.value[fieldName.value] = { value: 'optional' };
        });
    }

    async function save(event: ButtonStateChangedEvent) {
        if (!form.value) {
            return;
        }

        loading.value = true;

        form.value.requiredFields = [];
        form.value.optionalFields = [];

        Object.keys(fieldValues.value).forEach((fieldName) => {
            if (form.value === undefined) {
                return;
            }

            if (fieldValues.value[fieldName].value === 'required') {
                form.value.requiredFields.push(new GoalFieldName(fieldName));
            }

            if (fieldValues.value[fieldName].value === 'optional') {
                form.value.optionalFields.push(new GoalFieldName(fieldName));
            }
        });

        try {
            await client.dispatch(form.value);

            await load();

            notifyGoalSucces('Het is gelukt om het doel te wijzigen.');
        } catch (e) {
            notifyGoalFail('Er is iets misgegaan met het doel wijzigen, probeer het later opnieuw.');
        }

        loading.value = false;
        event.button.setState(ButtonState.completed);
        await router.push({ name: 'assistent-chat-edit-chat-connect', params: { assistentId: router.currentRoute.value.params.assistentId } });
    }

    function notifyGoalSucces(reason: string) {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: reason,
                color: 'green-500',
            },
            duration: 2000,
        });
    }

    function notifyGoalFail(reason: string) {
        notify({
            component: NotificationBlock,
            props: {
                title: 'Mislukt!',
                text: reason,
                color: 'red-500',
            },
            duration: 4000,
        });
    }

    onMounted(async () => {
        await load();
    });
</script>

<style scoped lang="scss">
    .goal-form__radio-header {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
</style>
